import { Injectable } from '@angular/core';
import { Observable, ReplaySubject, Subscription } from 'rxjs';
import { ProjectService } from './project.service';
import { isReportMode } from './chart-theme';
import {
  AdaptiveResolutionService,
  ringBufferHandle,
} from './adaptive-resolution.service';
import {
  DEFAULT_TICK_RETENTION,
  TelemetryRingBuffer,
} from './telemetry-ring-buffer';

/**
 * What changed, so a consumer does not have to infer it from buffer shape.
 *
 * A reset and an append look similar from outside — both move nextIndex — but
 * they demand different work: a backfill invalidates anything accumulated
 * against the previous window, while an append only needs the new rows folded
 * in. Guessing from indices is possible and fragile (a reset to a LARGER row set
 * looks exactly like an append), so the writer states it instead.
 */
export interface TelemetryChange {
  revision: number;
  kind: 'historical' | 'live' | 'replace' | 'clear';
  /** For 'live': how many rows were appended, and at which absolute index. */
  appended: number;
  /** Absolute index of the first appended row, for 'live'. */
  fromIndex: number;
}

/**
 * Which copy of a family's rows a caller means.
 *
 * 'live' is the running charts. 'report' is the PDF pre-render, which asks for
 * its OWN window and resolution and must never overwrite what the live charts
 * are showing — a report generated mid-cure used to leave report-resolution rows
 * in the live buffer. 'analytics' is a reader that aggregates across a series
 * without rendering it.
 *
 * A REGISTERED SET, NOT AN OPEN ONE. This was two ids and a note saying more
 * would be speculative. Two turned out to be the count of CONSUMERS THAT EXISTED,
 * not the count the problem has: a stats/aggregate reader and a server-side
 * report path are both live prospects, and both want rows without disturbing what
 * a chart is showing — which is exactly what a variant is for.
 *
 * Registering them here rather than opening the type to `string` keeps what a
 * variant may be a compile-time fact, so a typo cannot silently create a third
 * buffer that nothing ever reads or drops. Adding one is a line in this list;
 * everything downstream is keyed generically off it.
 */
export const TELEMETRY_VARIANTS = ['live', 'report', 'analytics'] as const;

export type VariantId = (typeof TELEMETRY_VARIANTS)[number];

/** Families that are FETCHED. heatMaps is derived per tab and is not one. */
export type TelemetryFamily = 'temperature' | 'zone' | 'loss';

export const TELEMETRY_FAMILIES: readonly TelemetryFamily[] = [
  'temperature',
  'zone',
  'loss',
];

/**
 * What a consumer may do with a shared buffer: everything except mutate it.
 *
 * TelemetryRingBuffer satisfies this structurally, so handing one out as a
 * ReadonlyTelemetryBuffer costs nothing at runtime while making the single-writer
 * rule a compile-time fact rather than a convention.
 */
export interface ReadonlyTelemetryBuffer<T> {
  readonly capacity: number;
  readonly firstIndex: number;
  readonly nextIndex: number;
  readonly totalCount: number;
  readonly retainedCount: number;
  readonly evictedCount: number;
  readonly hasEvicted: boolean;
  readonly last: T | undefined;
  has(absIndex: number): boolean;
  get(absIndex: number): T | undefined;
  nearest(absIndex: number): { row: T; index: number } | undefined;
  toArray(): T[];
  /** Walk the retained window with each row's ABSOLUTE index. */
  forEachRetained(fn: (item: T, absIndex: number) => void): void;
}

/**
 * ONE BUFFER PER TELEMETRY FAMILY, ONE WRITER.
 *
 * The problem
 * -----------
 * Four tab components each keep their own ring buffers of the same telemetry:
 * temperature is held three times over, loss four times, zone twice. Every copy
 * ingests the same ProjectService emissions independently, so the same rows are
 * retained up to four times and appended up to four times.
 *
 * Measured from the declarations: 4600 retained rows across the tabs against
 * 1800 if each family is held once — and the saving grows with the number of
 * tabs visited, because a tab's buffer stays alive until the component is
 * destroyed.
 *
 * The rule
 * --------
 * This service is the ONLY code permitted to mutate a family buffer. Components
 * read through ReadonlyTelemetryBuffer and re-render on changed$. That ordering
 * is guaranteed by construction: the write happens, then the notification. The
 * alternative — leaving components subscribed to the data streams and merely
 * deleting their write lines — would make correctness depend on RxJS subscription
 * registration order, which is invisible in review and fails as "renders one tick
 * stale" long after the change that caused it.
 *
 * heatMaps is deliberately NOT here. Nothing fetches it: on the temperature tab
 * it is derived from ZONE rows and on graph the waterfall is derived from
 * TEMPERATURE rows. It is a per-tab projection with two different definitions,
 * and sharing it would mean choosing one of them.
 *
 * Wired to nothing yet
 * --------------------
 * Step 1 of the rollout ships this inert: nothing subscribes to it and nothing
 * feeds it, so it cannot affect a running app. connect() is where the stream
 * wiring will go in step 2, one family at a time.
 */
@Injectable({ providedIn: 'root' })
export class TelemetryStoreService {
  // Keyed `${family}:${variant}` — see slot(). The live slots are created up
  // front; a report slot is created on demand and dropped when the report ends.
  private readonly buffers = new Map<string, TelemetryRingBuffer<unknown>>();
  private readonly changed = new Map<string, ReplaySubject<TelemetryChange>>();
  /** slot -> consumerId -> rows required. Capacity is the max of these. */
  private readonly required = new Map<string, Map<string, number>>();
  private readonly revisions = new Map<string, number>();

  private project = '';
  private suspendCount = 0;
  private suspendReasons: string[] = [];

  constructor(
    private projectService: ProjectService,
    private adaptive: AdaptiveResolutionService
  ) {
    for (const family of TELEMETRY_FAMILIES) {
      this.createSlot(this.slot(family, 'live'));
    }
  }

  /** The key for one family's rows in one variant. */
  private slot(family: TelemetryFamily, variant: VariantId): string {
    return `${family}:${variant}`;
  }

  /**
   * Which variant an un-qualified call means.
   *
   * Resolved from report mode rather than threaded through every caller. The
   * offscreen report components already consult isReportMode() to pick their
   * colours at construction, so reusing it means NO component signature changes
   * — which matters because this glue is the part that gets deleted if reporting
   * moves server-side.
   */
  private activeVariant: VariantId = 'live';

  /**
   * Serialises non-live variant scopes. See withVariant.
   *
   * Held as a promise chain rather than a boolean lock so a waiting caller
   * resumes on its own rather than polling or being rejected.
   */
  private variantQueue: Promise<unknown> = Promise.resolve();

  private current(): VariantId {
    // The explicit window wins over the ambient flag. setReportMode(false) is
    // called by whoever destroys the offscreen components, which happens AFTER
    // the pre-render returns — so the flag alone would leave reads resolving to
    // a variant whose slots have already been dropped.
    // The explicit variant wins whenever one is open, whichever it is. This read
    // used to name 'report' twice, which quietly made the registered set of
    // variants a set of one-plus-live.
    if (this.activeVariant !== 'live') {
      return this.activeVariant;
    }
    return isReportMode() ? 'report' : 'live';
  }

  private createSlot(key: string): void {
    this.buffers.set(key, new TelemetryRingBuffer<unknown>(DEFAULT_TICK_RETENTION));
    this.changed.set(key, new ReplaySubject<TelemetryChange>(1));
    this.required.set(key, new Map());
    this.revisions.set(key, 0);
  }

  // -------------------------------------------------------------- connecting

  private readonly connected = new Set<TelemetryFamily>();
  private readonly subscriptions: Subscription[] = [];
  private projectWatched = false;

  /**
   * Start ingesting a family. Idempotent — a second call is a no-op.
   *
   * Rolled out one family at a time deliberately. Until a family is connected
   * here, the components that own it keep their own buffers and their own writes,
   * so a regression during the conversion is attributable to ONE family rather
   * than to "the store".
   */
  connect(family: TelemetryFamily): void {
    this.watchProject();
    if (this.connected.has(family)) {
      return;
    }
    this.connected.add(family);

    const streams: Record<
      TelemetryFamily,
      { historical: Observable<{ rows: unknown[] }>; live: Observable<{ rows: unknown[] }> }
    > = {
      loss: {
        historical: this.rowsOf(this.projectService.lossData, (d) => d?.LRC),
        live: this.rowsOf(this.projectService.lossRTData, (d) => d?.LRC),
      },
      temperature: {
        historical: this.rowsOf(this.projectService.temperatureData, (d) => d?.TRC),
        live: this.rowsOf(this.projectService.temperatureRTData, (d) => d?.TRC),
      },
      zone: {
        historical: this.rowsOf(this.projectService.zoneData, (d) => d?.ZRC),
        live: this.rowsOf(this.projectService.zoneRTData, (d) => d?.ZRC),
      },
    };

    // ADAPTIVE OWNS A SHRINK, SO IT IS A WRITER TOO.
    //
    // ringBufferHandle exposes shrinkTo, which the adaptive service calls under
    // memory pressure. Leaving that registration with the components would mean
    // four handles onto one shared buffer, each able to shrink it, which is the
    // single-writer rule broken by the back door. The store registers once per
    // family and the components stop registering theirs.
    const widthOf: Record<TelemetryFamily, (row: any) => number> = {
      loss: (r) => r?.LR?.length ?? 0,
      temperature: (r) => r?.TR?.length ?? 0,
      zone: (r) => r?.ZR?.length ?? 0,
    };
    this.adaptive.registerBuffer(
      ringBufferHandle(
        `store.${family}`,
        this.bufferFor(family),
        widthOf[family]
      )
    );

    const { historical, live } = streams[family];
    this.subscriptions.push(
      historical.subscribe(({ rows }) => {
        // An empty backfill is the BehaviorSubject seed, not a payload. Applying
        // it would clear a live buffer on subscribe.
        if (rows.length > 0) {
          this.applyHistorical(family, rows);
        }
      })
    );
    this.subscriptions.push(
      live.subscribe(({ rows }) => {
        if (rows.length > 0) {
          this.appendLive(family, rows);
        }
      })
    );
    console.log(`TELEMETRY-STORE connected ${family}`);
  }

  /** True once this family is fed by the store rather than by its components. */
  isConnected(family: TelemetryFamily): boolean {
    return this.connected.has(family);
  }

  private rowsOf<T>(
    source: Observable<T>,
    pick: (payload: T) => unknown[] | null | undefined
  ): Observable<{ rows: unknown[] }> {
    return new Observable<{ rows: unknown[] }>((sub) =>
      source.subscribe({
        next: (payload) => sub.next({ rows: pick(payload) ?? [] }),
        error: (e) => sub.error(e),
      })
    );
  }

  private watchProject(): void {
    if (this.projectWatched) {
      return;
    }
    this.projectWatched = true;
    this.subscriptions.push(
      this.projectService.selectedProject.subscribe((name) =>
        this.selectProject(name)
      )
    );
  }

  // ---------------------------------------------------------------- reading

  /** The shared buffer for a family. Readers cannot mutate it. */
  read<T>(
    family: TelemetryFamily,
    variant: VariantId = this.current()
  ): ReadonlyTelemetryBuffer<T> {
    return this.bufferFor(family, variant) as unknown as ReadonlyTelemetryBuffer<T>;
  }

  /**
   * Fires AFTER the buffer changes, and REPLAYS the most recent change to a new
   * subscriber.
   *
   * The replay is not a convenience, it is the whole contract. Tabs mount long
   * after the data they display arrived — the backfill lands during app start and
   * the user clicks through to /health minutes later — so a plain Subject hands a
   * late subscriber nothing at all, and every piece of state it derives from the
   * rows silently never gets built. That is exactly what happened on the first
   * /health conversion: the fault index sat at lastFoldedIndex = -1 with 1363
   * rows in the buffer, because the historical notification had been emitted
   * before the component existed to hear it.
   *
   * The old per-tab code did not have this problem by accident: it read from
   * ProjectService BehaviorSubjects, which replay by nature. Anything replacing
   * them has to replay too.
   *
   * Safe for every kind. A replayed 'live' makes a fresh consumer fold forward
   * from its own empty high-water mark, which walks the whole retained window —
   * the same end state as a rebuild.
   *
   * The revision lets a consumer tell "nothing happened" from "changed back to
   * the same shape" without diffing rows.
   */
  changed$(
    family: TelemetryFamily,
    variant: VariantId = this.current()
  ): Observable<TelemetryChange> {
    return this.subjectFor(family, variant).asObservable();
  }

  /** Current revision, for a consumer that renders on demand rather than on push. */
  revision(family: TelemetryFamily, variant: VariantId = this.current()): number {
    return this.revisions.get(this.slot(family, variant)) ?? 0;
  }

  // ---------------------------------------------------------------- writing

  /**
   * A historical backfill has arrived: the buffer becomes exactly these rows.
   *
   * Mirrors what each component did in its own xData.subscribe.
   */
  applyHistorical<T>(family: TelemetryFamily, rows: readonly T[]): boolean {
    if (this.isSuspended()) {
      console.log(
        `TELEMETRY-STORE suspended, dropping historical ${family} (${rows.length} rows): ` +
          this.suspendReasons.join(', ')
      );
      return false;
    }
    const variant = this.current();
    this.bufferFor(family, variant).reset(rows as readonly unknown[]);
    this.bump(family, 'historical', variant);
    return true;
  }

  /**
   * Live ticks: append in order.
   *
   * Returns the number appended, which is what a caller needs to advance a tick
   * counter — a fixed +1 drifts whenever a poll interval spans more than one tick.
   */
  appendLive<T>(family: TelemetryFamily, rows: readonly T[]): number {
    if (this.isSuspended()) {
      console.log(
        `TELEMETRY-STORE suspended, dropping ${rows.length} live ${family} row(s)`
      );
      return 0;
    }
    if (rows.length === 0) {
      return 0;
    }
    // LIVE TICKS ALWAYS GO TO THE LIVE VARIANT, even while a report is running.
    // The socket keeps delivering during a pre-render, and those rows belong to
    // the charts the user is looking at — not to the report's private window.
    const buffer = this.bufferFor(family, 'live');
    // Captured BEFORE the pushes: consumers that index derived state by absolute
    // tick — the fault index does — need where these rows landed, and after the
    // loop that information is gone.
    const fromIndex = buffer.nextIndex;
    for (const row of rows) {
      buffer.push(row as unknown);
    }
    if (this.activeVariant !== 'live') {
      // A report is running; hold the notification (see flushDeferredLive).
      this.deferredLive.add(family);
    } else {
      this.bump(family, 'live', 'live', rows.length, fromIndex);
    }
    return rows.length;
  }

  /**
   * Replace one retained row — the windowed refetch splicing full-resolution rows
   * over decimated ones. A no-op for an evicted tick, which is correct: it is
   * outside the retention window and cannot be displayed.
   */
  replace<T>(family: TelemetryFamily, absIndex: number, row: T): boolean {
    if (this.isSuspended()) {
      return false;
    }
    const variant = this.current();
    const replaced = this.bufferFor(family, variant).replace(absIndex, row as unknown);
    if (replaced) {
      this.bump(family, 'replace', variant);
    }
    return replaced;
  }

  // ------------------------------------------------------------- lifecycle

  /**
   * Point the store at a selection. A different project drops every buffer.
   *
   * Deliberately NOT tied to component teardown. Today ngOnDestroy clears a
   * tab's buffers, so leaving a tab throws its data away and returning refetches
   * it — the cross-tab redownload, one level below the request cache. Under this
   * store, leaving a tab unsubscribes from changed$ and touches no data.
   */
  selectProject(project: string): void {
    if (project === this.project) {
      return;
    }
    this.project = project;
    // Every slot, including any report slot still open — a report for the old
    // project describes nothing once the selection moves.
    for (const family of TELEMETRY_FAMILIES) {
      const buffer = this.bufferFor(family, 'live');
      // CLEARING AN EMPTY BUFFER IS NOT A CHANGE. It matters now that changed$
      // replays: the first selectProject moves from '' to a real name and would
      // otherwise leave every family holding a 'clear' that never described
      // anything, which each late subscriber would then replay and act on.
      if (buffer.totalCount === 0) {
        continue;
      }
      buffer.clear();
      this.bump(family, 'clear', 'live');
    }
    // The mark is per PROJECT: the rows are gone anyway, so the next project
    // starts from whatever its consumers declare rather than inheriting a wide
    // window from the last one.
    this.highWater.clear();
    for (const family of TELEMETRY_FAMILIES) {
      const key = this.slot(family, 'live');
      const wants = [...(this.required.get(key)?.values() ?? [])];
      // FORCED, in both directions. applyCapacity only ever grows — that is the
      // whole point of it — so the one place a window is allowed to narrow has
      // to say so explicitly.
      const target = wants.length ? Math.max(...wants) : DEFAULT_TICK_RETENTION;
      this.highWater.set(key, target);
      this.bufferFor(family, 'live').resizeTo(target);
    }
    // Drop EVERY non-live variant's slots, not just the report's — a new
    // selection invalidates any copy of the old project's rows, whoever holds it.
    for (const variant of TELEMETRY_VARIANTS) {
      if (variant !== 'live') {
        this.closeVariant(variant);
      }
    }
    console.log(`TELEMETRY-STORE cleared for project change -> ${project || '(none)'}`);
  }

  /** The selection these buffers hold. */
  get currentProject(): string {
    return this.project;
  }

  // ------------------------------------------------------------- retention

  /**
   * Declare how many rows a consumer needs. Capacity is the MAX across consumers.
   *
   * Curing asks for 200 where the other tabs ask for 600, so the shared buffer
   * holds 600 and curing renders its own window over it. Curing therefore retains
   * more than it did alone — while the family total falls from 1400 to 600,
   * because three copies became one.
   */
  requireRetention(
    family: TelemetryFamily,
    consumerId: string,
    rows: number,
    variant: VariantId = this.current()
  ): void {
    if (!Number.isFinite(rows) || rows < 1) {
      return;
    }
    const key = this.slot(family, variant);
    if (!this.required.has(key)) {
      this.createSlot(key);
    }
    this.required.get(key)?.set(consumerId, Math.floor(rows));
    this.applyCapacity(family, variant);
  }

  /** A consumer is gone; its requirement no longer holds the capacity up. */
  releaseRetention(
    family: TelemetryFamily,
    consumerId: string,
    variant: VariantId = this.current()
  ): void {
    this.required.get(this.slot(family, variant))?.delete(consumerId);
    this.applyCapacity(family, variant);
  }

  /**
   * GROW ON DEMAND, SHRINK ONLY ON PROJECT CHANGE.
   *
   * Capacity tracks the HIGH-WATER MARK of everything declared while a project
   * is open, not the current maximum. Following the current maximum downwards
   * was correct arithmetic and a visible regression: /curing declares 200 where
   * the other tabs declare 600, so a single visit to /curing shrank the SHARED
   * buffer and evicted 400 rows — and returning to /health restored the capacity
   * but not the rows, because eviction is permanent without a refetch. One tab
   * silently truncating another tab's history is exactly the class of quiet
   * regression this store exists to remove.
   *
   * The cost is holding the wide window (~3.2 MB per family) while only a narrow
   * consumer is mounted. That is a trivial price against losing data the user
   * can see, and the whole family still collapses from three or four buffers to
   * one.
   *
   * The mark resets on project change, where the rows are dropped anyway — see
   * selectProject.
   */
  private applyCapacity(family: TelemetryFamily, variant: VariantId): void {
    const key = this.slot(family, variant);
    const wants = [...(this.required.get(key)?.values() ?? [])];
    const currentMax = wants.length ? Math.max(...wants) : DEFAULT_TICK_RETENTION;
    const mark = Math.max(this.highWater.get(key) ?? 0, currentMax);
    this.highWater.set(key, mark);

    const buffer = this.bufferFor(family, variant);
    if (mark > buffer.capacity && buffer.resizeTo(mark)) {
      // Growing cannot evict, but it is still a capacity change consumers may
      // want to redraw against.
      this.bump(family, 'historical', variant);
    }
  }

  /** Widest retention declared for a slot since the project opened. */
  private highWater = new Map<string, number>();

  // --------------------------------------------------------------- suspend

  /**
   * Stop ingesting while something else is driving the ProjectService subjects.
   *
   * THE EXPORT PRE-RENDER. ExportPrerenderService instantiates real route
   * components offscreen and pushes its OWN windowed, report-resolution payloads
   * through setTempData and the zone/loss loaders. Those reach the same subjects
   * this store ingests, so a report generated during a live cure would overwrite
   * the shared buffers with report data.
   *
   * Worth being precise about blame: that hazard EXISTS TODAY — those emissions
   * already reach every mounted tab's buffers. Sharing does not create it, it
   * widens the blast radius from "mounted tabs" to "everything". This is the
   * minimal safe fix; routing the report through the store properly needs the
   * store to be resolution-aware, which is a separate piece of work.
   *
   * Counted rather than boolean, so nested or overlapping suspends cannot have
   * the inner one resume the outer one's work early.
   */
  suspend(reason: string): void {
    this.suspendCount++;
    this.suspendReasons.push(reason);
    console.log(`TELEMETRY-STORE suspended (${this.suspendCount}): ${reason}`);
  }

  resume(reason: string): void {
    if (this.suspendCount === 0) {
      console.warn(`TELEMETRY-STORE resume('${reason}') with nothing suspended`);
      return;
    }
    this.suspendCount--;
    const at = this.suspendReasons.indexOf(reason);
    if (at !== -1) {
      this.suspendReasons.splice(at, 1);
    }
    console.log(`TELEMETRY-STORE resumed (${this.suspendCount} left): ${reason}`);
  }

  isSuspended(): boolean {
    return this.suspendCount > 0;
  }

  // --------------------------------------------------------------- variants

  /**
   * Run `work` with reads and historical writes routed to `variant`.
   *
   * The variant is resolved from report mode rather than passed down, so nothing
   * inside `work` needs to know it is running for a report — including the
   * offscreen components, whose signatures do not change.
   *
   * The finally is the bounded lifetime: report buffers are dropped whether the
   * capture succeeded or threw. A pre-render has several failure paths — a fetch
   * timeout, a blank surface — and a leaked report slot would hold a second full
   * window of every family until the project changed.
   */
  async withVariant<T>(variant: VariantId, work: () => Promise<T>): Promise<T> {
    if (variant === 'live') {
      return work();
    }

    // ALREADY IN THIS VARIANT: run inline, do not open a second scope.
    //
    // A nested call naming the variant that is already open is asking for what it
    // already has. Queueing it behind the outer one would deadlock — the outer
    // cannot finish until the inner does, and the inner is waiting for the outer.
    if (this.activeVariant === variant) {
      return work();
    }

    // OTHERWISE SERIALISE. `activeVariant` is one field and the finally below
    // unconditionally restores 'live' and drops the variant's slots, so two
    // overlapping scopes corrupt each other: whichever finishes FIRST resets the
    // shared field while the other is still working, and the survivor's remaining
    // writes are routed by current() into the LIVE buffers — report rows rendered
    // into the user's charts, which is the exact failure the variant mechanism
    // was built to prevent.
    //
    // Serialising rather than refusing, because the concurrent case is one we
    // deliberately created: 'analytics' was registered so a reader could
    // aggregate a series WITHOUT disturbing the charts, i.e. to run alongside
    // something else. Refusing would make a legitimate reader fail for a reason
    // it cannot control or retry sensibly; waiting gives it the right answer, just
    // later. Nothing here is on a render path.
    //
    // Only the export path calls this today, and its button is disabled while
    // generating — so nothing currently overlaps. That is a property of the UI,
    // not of this store, and it is not one to depend on.
    const run = this.variantQueue.then(async () => {
      this.activeVariant = variant;
      try {
        return await work();
      } finally {
        this.activeVariant = 'live';
        this.closeVariant(variant);
        this.flushDeferredLive();
      }
    });

    // The chain must not carry a rejection forward: one failed report would
    // otherwise reject every variant scope opened after it. The error still
    // reaches THIS caller through `run`.
    this.variantQueue = run.then(
      () => undefined,
      () => undefined
    );

    return run;
  }

  /**
   * DEFER LIVE NOTIFICATIONS WHILE A REPORT IS RUNNING.
   *
   * Report mode is a GLOBAL flag, and live components read their rows through an
   * accessor evaluated on every use — `this.telemetryStore.read('loss')`. So a
   * live tick arriving mid-report would fire the live changed$, the component
   * would call its getter, and the getter would resolve to the REPORT variant and
   * render report data into a live chart. At one tick a minute against a report
   * that takes seconds, that is likely rather than theoretical.
   *
   * The rows still land in 'live' — nothing is dropped, which is the difference
   * between this and the suspend it replaces. Only the NOTIFICATION waits, and
   * one is emitted per family afterwards so the charts redraw from a buffer that
   * is once again the one they think they are reading.
   */
  private deferredLive = new Set<TelemetryFamily>();

  private flushDeferredLive(): void {
    const families = [...this.deferredLive];
    this.deferredLive.clear();
    for (const family of families) {
      this.bump(family, 'historical', 'live');
    }
    if (families.length > 0) {
      console.log(
        `TELEMETRY-STORE flushed deferred live notifications: [${families.join(', ')}]`
      );
    }
  }

  /** Drop a variant's slots entirely. Never applies to 'live'. */
  closeVariant(variant: VariantId): number {
    if (variant === 'live') {
      return 0;
    }
    let dropped = 0;
    for (const family of TELEMETRY_FAMILIES) {
      const key = this.slot(family, variant);
      if (this.buffers.has(key)) {
        this.buffers.get(key)?.clear();
        this.buffers.delete(key);
        this.changed.get(key)?.complete();
        this.changed.delete(key);
        this.required.delete(key);
        this.revisions.delete(key);
        dropped++;
      }
    }
    if (dropped > 0) {
      console.log(`TELEMETRY-STORE closed variant '${variant}' (${dropped} slot(s))`);
    }
    return dropped;
  }

  /** Slots currently held, for assertions and diagnostics. */
  slotKeys(): string[] {
    return [...this.buffers.keys()].sort();
  }

  /**
   * Run `work` with ingestion suspended, resuming even if it throws.
   *
   * The pre-render has several failure paths — a fetch timeout, a capture that
   * finds a blank surface — and a suspend that leaks on any of them would freeze
   * the live charts silently until reload.
   */
  async whileSuspended<T>(reason: string, work: () => Promise<T>): Promise<T> {
    this.suspend(reason);
    try {
      return await work();
    } finally {
      this.resume(reason);
    }
  }

  // ---------------------------------------------------------------- private

  private bufferFor(
    family: TelemetryFamily,
    variant: VariantId = this.current()
  ): TelemetryRingBuffer<unknown> {
    const key = this.slot(family, variant);
    let buffer = this.buffers.get(key);
    if (!buffer) {
      // A report slot is created on first use and dropped by closeVariant, so a
      // report that never runs costs nothing.
      this.createSlot(key);
      buffer = this.buffers.get(key);
    }
    if (!buffer) {
      throw new Error(`TelemetryStore: unknown slot ${key}`);
    }
    return buffer;
  }

  private subjectFor(
    family: TelemetryFamily,
    variant: VariantId = this.current()
  ): ReplaySubject<TelemetryChange> {
    const key = this.slot(family, variant);
    if (!this.changed.has(key)) {
      this.createSlot(key);
    }
    const subject = this.changed.get(key);
    if (!subject) {
      throw new Error(`TelemetryStore: unknown slot ${key}`);
    }
    return subject;
  }

  /** Advance the revision and notify — always AFTER the buffer has changed. */
  private bump(
    family: TelemetryFamily,
    kind: TelemetryChange['kind'],
    variant: VariantId = this.current(),
    appended = 0,
    fromIndex = -1
  ): void {
    const key = this.slot(family, variant);
    const revision = (this.revisions.get(key) ?? 0) + 1;
    this.revisions.set(key, revision);
    this.subjectFor(family, variant).next({ revision, kind, appended, fromIndex });
  }
}
